import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classNames from 'classnames';

import colors from './colors.css';
import sizes from './sizes.css';
import textAligns from './text_align.css';

/**
 * Simple style proxy High Order Component for manipulating properties.
 * it is used for:
 *     - adding basic styles in classNames for color, size, textAlign.
 *     - adding 'variant' prefix for passed in variant prop
 *     - passing through all the other props to wrapped components
 * See more here: https://medium.com/@franleplant/react-higher-order-components-in-depth-cf9032ee6c3e#.9hz9hnh84
 * @param {Object} WrappedComponent - Wrapped React Component.
 * @returns {Object} - return a React element.
 */
const withStyleProxy = (WrappedComponent) => {
    class WithStyleProxy extends PureComponent {

        /**
         * Render the component.
         * @returns {Object} - Returns the Wrapped Component.
         */
        render = () => {
            const { className, color, size, textAlign, variant, forwardedRef, ...rest } = this.props;

            const classArray = [];
            if (color) {
                classArray.push(colors[`color_${color}`] || 'undefined');
            }
            if (size) {
                classArray.push(sizes[`size_${size}`] || 'undefined');
            }
            if (textAlign) {
                classArray.push(textAligns[`text_align_${textAlign}`] || 'undefined');
            }
            // Process the classes.
            const classes = classNames(className, classArray);
            // Render the component.
            return <WrappedComponent className={classes} ref={forwardedRef} variant={variant ? `variant_${variant}` : undefined} {...rest} />;
        };

    }

    WithStyleProxy.propTypes = {
        className: PropTypes.string,
        /**
         * Variant of the component.
         */
        variant: PropTypes.string,
        /**
         * Visual color of the element.
         */
        color: PropTypes.string,
        /**
         * Size of the element.
         */
        size: PropTypes.string,
        forwardedRef: PropTypes.object,
        /**
         * Text alignment - only works on block elements.
         */
        textAlign: PropTypes.oneOf(['inherit', 'initial', 'left', 'right', 'center', 'justify'])
    };

    const withStyleProxyComponent = React.forwardRef((props, ref) => {
        return ref == null ? <WithStyleProxy {...props} /> : <WithStyleProxy {...props} forwardedRef={ref} />;
    });

    /**
     * This is a workaround on enzyme's limitation working with forwardRef wrapped HOC
     * This tells enzyme that the component it is still refering to is the WithStyleProxy
     * and not a ForwardRef component wrapper. ie the ForwardRef is sort of ignored (as it was renamed to WithStyleProxy)
     *
     * https://stackoverflow.com/questions/56463619/how-to-test-forwardrwf-components-with-jest-enzyme
     * https://legacy.reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging
     * https://github.com/enzymejs/enzyme/issues/1810
     */
    withStyleProxyComponent.displayName = 'WithStyleProxy';

    return withStyleProxyComponent;
};

export { withStyleProxy };

/**
 * This enzyme-to-json mapper excludes the ForwardRef component from enzyme render()
 * This is to avoid the double ups WithStyleProxy component. We aren't too concern with ForwardRef.
 * In fact most of our existing test would not expect it to show. This just makes our efforts backwards compatible.
 *
 * Idea taken from: https://github.com/enzymejs/enzyme/issues/2190#issuecomment-592452679
 *
 * @see https://github.com/adriantoine/enzyme-to-json/blob/HEAD/docs/map.md#output-mapping-feature
 * @param {Object} node - Enzyme Output Mapper
 * @returns {Object} node to be included in snapshot
 */
export const enzymeMapOmitWithStyleProxyForwardRefOnRender = (node) => {
    if (node.node?.type?.$$typeof === Symbol.for('react.forward_ref')) {
        if (node.children && node.children[0]?.type === 'WithStyleProxy') {
            return node.children[0];
        }
    }

    return node;
};
